/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { system, position, layout, space } from 'styled-system';
import commonRoutes from '../../routes/common';
import { assetize } from '../../utils/pureUtils';
import Box from '../atoms/Box';
import Img from '../atoms/Img';

import Text from '../atoms/Text';
import { useTranslate, translateArray } from '../../translate/I18n';
import Link from '../atoms/Link.js';
import Testimonials from './Testimonials.js';
import SanitizedHTML from '../elements/SanitizedHTML.js';
import ButtonGradient from '../atoms/ButtonGradient.js';
import MinimalMissionCard from './MinimalMissionCard';
import BackToTop from './BackToTop';

const {
  general: { register }
} = commonRoutes;

const StyledLink = styled(Link)({
  background: '#15795D',
  margin: '40px auto 0',
  display: 'inline-block',
  padding: '8px 16px',
  borderRadius: '8px',
  color: '#fff',
  fontWeight: '700',
  transition: 'background .3s ease',
  '&:hover': {
    background: '#0C4535'
  }
});

const StyledWhiteButton = styled(Link)({
  background: '#fff',
  margin: '40px auto 0',
  display: 'inline-block',
  border: '1px solid #D5D7D9',
  boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  padding: '8px 16px',
  borderRadius: '8px',
  color: '#111517',
  fontWeight: '700',
  transition: 'background .3s ease',
  '&:hover': {
    background: '#F3F4F6'
  }
});

const StyledWave = styled('svg')({
  position: 'absolute',
  top: '-22%',
  height: '420px',
  width: '100%',
  left: '-10px',
  right: 0,
  zIndex: '-1',
  '@media (max-width: 1240px)': {
    top: 0,
    height: '200px'
  }
});

const StyledGlassCard = styled('div')(
  ({ theme }) => ({
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.75)',
    backdropFilter: 'blur(12px)',
    boxShadow: '0 10px 40px 0 rgba(64,64,64,0.10)',
    overflow: 'hidden',
    height: '52px',
    padding: theme.space[1],
    display: 'inline-block'
  }),
  system({
    transform: true,
    opacity: true,
    transition: true
  }),
  position,
  layout,
  space
);

const section2ImageNames = ['linkedin', 'settings', 'search-matching'];
const section3ImageNames = ['discord', 'discount', 'fist'];

const HomePageFreelance = () => {
  const { t } = useTranslate();
  const translatedRegisterRoute = register.to;
  const [scroll, setScroll] = useState(0);

  const scrollHandler = () => {
    const scroll = document.body.scrollTop || document.documentElement.scrollTop;

    setScroll(scroll);
  };

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  });

  return (
    <Box bg="#F3F4F6" pb="120px">
      <BackToTop />
      <Box
        textAlign="center"
        py="8%"
        overflow={{ _: 'hidden', xl: 'visible' }}
        background="linear-gradient(180deg, #FFFFFF 0%, #F3F4F6 100%)"
        position="relative">
        <Box maxWidth="792px" mx="auto">
          <Text tag="h1" styleName="wH1" mb={4}>
            {t('homePageFreelancer:header:title')}
          </Text>
          <ButtonGradient as={Link} data-cy="homePageFreelance-signUp" color="#ffffff" to={translatedRegisterRoute}>
            {t('homePageFreelancer:header:cta')}
          </ButtonGradient>
        </Box>
        <Box position="absolute" left={{ _: '-112px', xl: '3%' }} top="28%" display={{ _: 'none', lg: 'block' }}>
          <Img
            alt="3D objects illustration"
            src={assetize('/homepage/bg-left-2.webp')}
            srcRetina={assetize('/homepage/bg-left-2@2x.webp')}
            fallback={assetize('/homepage/bg-left-2.png')}
            fallbackRetina={assetize('/homepage/bg-left-2@2x.png')}
            lazyloading={true}
          />
        </Box>

        <Box position="absolute" right={{ _: '-122px', xl: '5%' }} top="5%" display={{ _: 'none', lg: 'block' }}>
          <Img
            alt="3D objects illustration"
            src={assetize('/homepage/bg-right-2.webp')}
            srcRetina={assetize('/homepage/bg-right-2@2x.webp')}
            fallback={assetize('/homepage/bg-right-2.png')}
            fallbackRetina={assetize('/homepage/bg-right-2@2x.png')}
            lazyloading={true}
          />
        </Box>
      </Box>

      <Box maxWidth="1240px" mx="auto" px={{ _: 1, sm: 2, md: 3, lg: 4 }}>
        <Box maxWidth="783px">
          <Text tag="h2" styleName="wH2b" mb="3">
            {t('homePageFreelancer:section1:title')}
          </Text>
          <Text color="#424547" fontSize="16px" fontWeight="500" mb="5">
            {t('homePageFreelancer:section1:description')}
          </Text>
        </Box>

        <Box display="flex" gap="24px" mb="3" flexDirection={{ _: 'column', md: 'row' }}>
          <Box bg="white" borderRadius="2" p="3" flex="5">
            <Text fontSize="24px" lineHeight="28px" fontWeight="700" mb="24px">
              {t('homePageFreelancer:section1:items:0:title')}
            </Text>
            <Text color="#424547" fontSize="16px" fontWeight="500">
              {t('homePageFreelancer:section1:items:0:description')}
            </Text>
          </Box>

          <Box bg="white" borderRadius="2" p="3" flex="4">
            <Text fontSize="24px" lineHeight="28px" fontWeight="700" mb="24px">
              {t('homePageFreelancer:section1:items:1:title')}
            </Text>
            <Text color="#424547" fontSize="16px" fontWeight="500">
              {t('homePageFreelancer:section1:items:1:description')}
            </Text>

            <Box pt="4" pb="2" textAlign="center" position="relative">
              <Box
                background="linear-gradient(180deg, #10B981 0%, #047857 100%)"
                borderRadius="110px"
                height="110px"
                m="auto"
                width="110px"
              />
              <StyledGlassCard
                position="absolute"
                left="0"
                right="0"
                bottom="0"
                m="auto"
                opacity={1 / (scroll * 0.00166) - 0.8}
                transition="opacity .2s ease"
                borderRadius="2"
                width="140px"
                height="140px"
              />
            </Box>
          </Box>
        </Box>

        <Box mb={{ _: 8, lg: 20 }} display="flex" gap="24px" flexDirection={{ _: 'column', lg: 'row' }}>
          <Box
            bg="white"
            borderRadius="2"
            p="3"
            flex="4"
            position="relative"
            zIndex="1"
            display="flex"
            flexDirection="column"
            justifyContent="space-between">
            <Box position="absolute" top="0px" left="0" zIndex="-1">
              <Img
                alt={'floating ball decoration'}
                src={assetize('/homepage/bg-card2.webp')}
                srcRetina={assetize('/homepage/bg-card2@2x.webp')}
                fallback={assetize('/homepage/bg-card2.png')}
                fallbackRetina={assetize('/homepage/bg-card2@2x.png')}
                lazyloading={true}
              />
            </Box>

            <Box display="flex" justifyContent="space-around" mb={{ _: '16', lg: '0' }}>
              <StyledGlassCard transform="translateY(70px)">
                <Img
                  alt="deloitte logo"
                  src={assetize('/partner-clients/deloitte.webp')}
                  srcRetina={assetize('/partner-clients/deloitte@2x.webp')}
                  fallback={assetize('/partner-clients/deloitte.png')}
                  fallbackRetina={assetize('/partner-clients/deloitte@2x.png')}
                  lazyloading={true}
                />
              </StyledGlassCard>

              <StyledGlassCard transform="translateY(30px)">
                <Img
                  alt="strobbo logo"
                  src={assetize('/partner-clients/strobbo.webp')}
                  srcRetina={assetize('/partner-clients/strobbo@2x.webp')}
                  fallback={assetize('/partner-clients/strobbo.png')}
                  fallbackRetina={assetize('/partner-clients/strobbo@2x.png')}
                  lazyloading={true}
                />
              </StyledGlassCard>

              <StyledGlassCard transform="translateY(100px)">
                <Img
                  alt="publicis logo"
                  src={assetize('/partner-clients/publicis.webp')}
                  srcRetina={assetize('/partner-clients/publicis@2x.webp')}
                  fallback={assetize('/partner-clients/publicis.png')}
                  fallbackRetina={assetize('/partner-clients/publicis@2x.png')}
                  lazyloading={true}
                />
              </StyledGlassCard>

              <StyledGlassCard transform="translateY(56px)">
                <Img
                  alt="maltem logo"
                  src={assetize('/partner-clients/maltem.webp')}
                  srcRetina={assetize('/partner-clients/maltem@2x.webp')}
                  fallback={assetize('/partner-clients/maltem.png')}
                  fallbackRetina={assetize('/partner-clients/maltem@2x.png')}
                  lazyloading={true}
                />
              </StyledGlassCard>
              <StyledGlassCard transform="translateY(140px)">
                <Img
                  alt="labbox logo"
                  src={assetize('/partner-clients/labbox.webp')}
                  srcRetina={assetize('/partner-clients/labbox@2x.webp')}
                  fallback={assetize('/partner-clients/labbox.png')}
                  fallbackRetina={assetize('/partner-clients/labbox@2x.png')}
                  lazyloading={true}
                />
              </StyledGlassCard>
            </Box>
            <Box>
              <Text fontSize="24px" lineHeight="28px" fontWeight="700" mb="24px">
                {t('homePageFreelancer:section1:items:2:title')}
              </Text>
              <Text color="#424547" fontSize="16px" fontWeight="500">
                {t('homePageFreelancer:section1:items:2:description')}
              </Text>
            </Box>
          </Box>

          <Box bg="white" borderRadius="2" flex="5" overflow="hidden" position="relative" zIndex="1">
            <Box p="3">
              <Text fontSize="24px" lineHeight="28px" fontWeight="700" mb="24px">
                {t('homePageFreelancer:section1:items:3:title')}
              </Text>
              <Text color="#424547" fontSize="16px" fontWeight="500">
                {t('homePageFreelancer:section1:items:3:description')}
              </Text>
            </Box>
            <Box transform="rotate(-7deg)" mt="4" mb="-24px">
              <Box transform={`translateX(${scroll / 30 - 50}px)`} transition="transform .2s ease" whiteSpace="nowrap">
                {translateArray(t, 'homePageFreelancer:mission:examples').map(
                  ({ title, skill1, skill1Level, skill2, skill2Level }, index) => {
                    if (index < 3)
                      return (
                        <MinimalMissionCard
                          title={title}
                          skill1={skill1}
                          skill1Level={skill1Level}
                          skill2={skill2}
                          skill2Level={skill2Level}
                          key={title + index}
                        />
                      );
                    return '';
                  }
                )}
              </Box>
              <Box
                transform={`translateX(-${scroll / 30 + 260}px)`}
                pl="20"
                transition="transform .2s ease"
                whiteSpace="nowrap"
                mt="2">
                {translateArray(t, 'homePageFreelancer:mission:examples').map(
                  ({ title, skill1, skill1Level, skill2, skill2Level }, index) => {
                    if (index >= 3)
                      return (
                        <MinimalMissionCard
                          title={title}
                          skill1={skill1}
                          skill1Level={skill1Level}
                          skill2={skill2}
                          skill2Level={skill2Level}
                          key={title + index}
                        />
                      );
                    return '';
                  }
                )}
              </Box>
            </Box>
            <Box position="absolute" bottom="-5px" right="0" zIndex="-1">
              <Img
                alt={'floating balls decoration'}
                src={assetize('/homepage/bg-card.webp')}
                srcRetina={assetize('/homepage/bg-card@2x.webp')}
                fallback={assetize('/homepage/bg-card.png')}
                fallbackRetina={assetize('/homepage/bg-card@2x.png')}
                lazyloading={true}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box position="relative" zIndex="1">
        <StyledWave
          viewBox="0 0 1442 411"
          version="1.1"
          zIndex="-1"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          xmlnsXlink="http://www.w3.org/1999/xlink">
          <g id="Freelance-Public-Page" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <g id="Freelance-page" transform="translate(7.000000, -3131.000000)">
              <g id="Group-11" transform="translate(-6.593050, 3131.321464)">
                <path
                  d="M6.1653522,0 C243.971612,111.764268 732.424467,57.2044663 979.59305,57.2044663 C1127.23369,57.2044663 1282.91785,80.8396862 1446.64552,128.110126 C1446.59305,128.110126 1446.59305,335.489693 1446.59932,335.489693 C1218.12125,300.823435 706.813107,255.548359 478.844651,254.154106 C250.876195,252.759852 52.2439809,310.062129 0,398.352768 C0,398.352768 6.1653522,0 6.1653522,0 Z"
                  id="Path-2"
                  fill="#10B981"></path>
                <path
                  d="M450.042503,48.7471598 C730.59305,48.7471598 900.210336,125.159742 1073.59305,136.703147 C1183.38605,144.012902 1307.71974,128.10103 1446.59412,88.967532 C1446.59412,88.967532 1446.59412,346.903778 1446.48598,346.903778 C1223.78618,278.12968 1085.51241,243.14281 857.543955,241.748557 C629.575499,240.354303 556.824744,244.617696 366.44736,280.468194 C176.069976,316.318693 51.8936418,371.617153 6.34728022,409.969997 C6.1653522,409.969997 6.59305038,94.0861407 6.1653522,94.0861407 C131.741078,60.6318902 318.580645,48.7471598 450.042503,48.7471598 Z"
                  id="Path-2"
                  fill="#047857"></path>
              </g>
            </g>
          </g>
        </StyledWave>
        <Testimonials role="freelancer" />
      </Box>

      <Box my={{ _: 8, lg: 20 }} maxWidth="1240px" mx="auto" px={{ _: 1, sm: 2, md: 3, lg: 4 }}>
        <Text tag="h2" styleName="wH2b" mb="5">
          {t('homePageFreelancer:section2:title')}
        </Text>
        <Box display="grid" gridGap="4" gridTemplateColumns={{ _: 'repeat(1, 1fr)', lg: 'repeat(3, 1fr)' }}>
          {translateArray(t, 'homePageFreelancer:section2:items').map(({ title, description, cta }, index) => (
            <Box
              bg="white"
              borderRadius="2"
              p={3}
              key={title}
              display="flex"
              flexDirection="column"
              justifyContent="space-between">
              <span>
                <Text fontSize="40px" fontWeight="600" mb={3}>
                  {index + 1}
                </Text>
                <Text tag="h4" fontSize="24px" lineHeight="28px" mb="2">
                  {title}
                </Text>
                <Text color="#424547" fontSize="16px" fontWeight="500">
                  {description}
                </Text>
              </span>
              <Box textAlign="center" mt={5}>
                <Img
                  alt={`${section2ImageNames[index]} illustration`}
                  src={assetize(`/homepage/${section2ImageNames[index]}.webp`)}
                  srcRetina={assetize(`/homepage/${section2ImageNames[index]}@2x.webp`)}
                  fallback={assetize(`/homepage/${section2ImageNames[index]}.png`)}
                  fallbackRetina={assetize(`/homepage/${section2ImageNames[index]}@2x.png`)}
                  lazyloading={true}
                />
                {cta && (
                  <Box textAlign="center">
                    <StyledLink to={register.to}>{cta}</StyledLink>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <Box maxWidth="1240px" mx="auto" px={{ _: 1, sm: 2, md: 3, lg: 4 }}>
          <Text tag="h2" styleName="wH2b" mb="3">
            {t('homePageFreelancer:section3:title')}
          </Text>
          <Text color="#424547" fontSize="16px" fontWeight="500" mb="7">
            {t('homePageFreelancer:section3:subtitle')}
          </Text>

          <Box display="flex" gap="24px" alignItems="start" mb="188px" flexDirection={{ _: 'column', lg: 'row' }}>
            {translateArray(t, 'homePageFreelancer:section3:items').map(
              ({ title, description, cta, ctaPath }, index) => (
                <Box
                  bg="white"
                  borderRadius="3"
                  key={title}
                  px="3"
                  py="4"
                  mt={index % 2 === 0 ? 4 : 0}
                  flex="1"
                  width={{ _: '100%', lg: 'auto' }}
                  maxWidth="460px"
                  ml="auto"
                  mr="auto">
                  <Box textAlign="center">
                    <Img
                      alt={`${section3ImageNames[index]} illustration`}
                      src={assetize(`/homepage/${section3ImageNames[index]}.webp`)}
                      srcRetina={assetize(`/homepage/${section3ImageNames[index]}@2x.webp`)}
                      fallback={assetize(`/homepage/${section3ImageNames[index]}.png`)}
                      fallbackRetina={assetize(`/homepage/${section3ImageNames[index]}@2x.png`)}
                      lazyloading={true}
                    />
                  </Box>
                  <Text tag="h4" fontSize="24px" lineHeight="30px" fontWeight="700" mb={3} mt={5}>
                    {title}
                  </Text>
                  <SanitizedHTML tagElement="div" content={description} />
                  {cta && (
                    <Box textAlign="center">
                      <StyledWhiteButton to={ctaPath} target="_blank" rel="noopener noreferrer">
                        {cta}
                      </StyledWhiteButton>
                    </Box>
                  )}
                </Box>
              )
            )}
          </Box>
        </Box>
      </Box>

      <Box maxWidth="1240px" mx="auto" px={{ _: 1, sm: 2, md: 3, lg: 4 }}>
        <Box textAlign="center" py="9" bg="white" borderRadius="2" position="relative" overflow="hidden">
          <Text tag="h2" styleName="wH2b" mb="3">
            {t('homePageFreelancer:footer:title')}
          </Text>
          <Text fontSize="18px" lineHeight="24px" fontWeight="400" mb="6" maxWidth="717px" mx="auto">
            {t('homePageFreelancer:footer:description')}
          </Text>
          <ButtonGradient as={Link} mt={2} to={register.to}>
            {t('homePageFreelancer:footer:cta')}
          </ButtonGradient>
          <Box
            position="absolute"
            bottom="-5px"
            left="0"
            overflow="hidden"
            display={{ _: 'none', md: 'block' }}
            width="250px">
            <Img
              src={'/assets/waves/waveCorner1.webp'}
              srcRetina={'/assets/waves/waveCorner1@2x.webp'}
              fallback={'/assets/waves/waveCorner1.png'}
              fallbackRetina={'/assets/waves/waveCorner1@2x.png'}
              alt="section wave decoration"
              lazyloading={false}
            />
          </Box>

          <Box
            position="absolute"
            bottom="-5px"
            right="0"
            overflow="hidden"
            display={{ _: 'none', md: 'block' }}
            width="350px">
            <Img
              src={'/assets/waves/waveCorner2.webp'}
              srcRetina={'/assets/waves/waveCorner2@2x.webp'}
              fallback={'/assets/waves/waveCorner2.png'}
              fallbackRetina={'/assets/waves/waveCorner2@2x.png'}
              alt="section wave decoration"
              lazyloading={false}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HomePageFreelance;
